import axios from 'axios'
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  CircleCheck,
  Clock,
  Eye,
  Phone,
  Search,
  SearchX,
  Users,
  X,
} from 'lucide-react'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { constants } from '../Constants'
import { AlertDialogDeleteUser } from '../components/AlertDialogs/AlertDialogDeleteUser'
import { AlertDialogResendPassword } from '../components/AlertDialogs/AlertDialogResendPassword'
import { AlertDialogUpdatePayment } from '../components/AlertDialogs/AlertDialogUpdatePayment'
import { Avatar } from '../components/ui'
import { PageLoader } from '../components/ui/Spinner/Spinner'
import '../styles/shared.scss'
import './UserTable.scss'

const PAYMENT_FILTERS = [
  { value: '', label: 'Todos', icon: <Users size={15} /> },
  { value: 'true', label: 'Pagados', icon: <CircleCheck size={15} /> },
  { value: 'false', label: 'Pendientes', icon: <Clock size={15} /> },
]

export const UserTable = () => {
  const [filter, setFilter] = useState('')
  const [appliedFilter, setAppliedFilter] = useState('')
  const [paymentFilter, setPaymentFilter] = useState('')
  const [users, setUsers] = useState([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [totalDocs, setTotalDocs] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const abortRef = useRef(null)

  useEffect(() => {
    // Cancelar petición anterior si el usuario filtra rápido
    if (abortRef.current) abortRef.current.abort()
    abortRef.current = new AbortController()

    const doFetch = async () => {
      setLoading(true)
      try {
        const params = { page, limit: pageSize }
        if (appliedFilter) params.search = appliedFilter
        if (paymentFilter) params.hasPaid = paymentFilter
        const { data } = await axios.get(
          `${constants.urlLocal}getUsersPaginated`,
          { params, signal: abortRef.current.signal }
        )
        setUsers(data.users)
        setTotalDocs(data.totalDocs)
        setTotalPages(data.totalPages)
      } catch (error) {
        if (axios.isCancel(error)) return
        console.error('Error al obtener los usuarios', error)
      }
      setLoading(false)
    }

    doFetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, appliedFilter, paymentFilter, refreshKey])

  const handleSearch = () => {
    setAppliedFilter(filter)
    setPage(1)
  }

  const handleClear = () => {
    setFilter('')
    setAppliedFilter('')
    setPage(1)
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter') handleSearch()
  }

  const updatePayment = async (
    userId,
    hasPaid,
    paymentDate,
    paymentAmount,
    adminId,
    adminName,
    adminLastname
  ) => {
    try {
      const response = await axios.put(`${constants.urlLocal}updatePayment`, {
        userId,
        hasPaid,
        paymentDate,
        paymentAmount,
        paymentDueDate: moment(paymentDate).add(1, 'month').toDate(),
        adminId,
        adminName,
        adminLastname,
      })
      if (response.data.ok) setRefreshKey(k => k + 1)
    } catch (error) {
      console.error('Error al actualizar el pago: ', error)
    }
  }

  const getLastPaymentDate = paymentHistory => {
    if (paymentHistory?.length > 0) {
      const sorted = [...paymentHistory].sort(
        (a, b) => new Date(b.paymentDate) - new Date(a.paymentDate)
      )
      return new Date(sorted[0].paymentDate).toLocaleDateString('es-ES', {
        month: 'long',
        year: 'numeric',
      })
    }
    return null
  }

  if (loading && users.length === 0)
    return (
      <div className="loading-center">
        <PageLoader />
      </div>
    )

  return (
    <div className="page-container">
      {/* Header */}
      <div className="ut-header">
        <h2 className="ut-header__title">
          Usuarios
          <span className="ut-header__count">{totalDocs}</span>
        </h2>
      </div>

      {/* Toolbar: search + filter chips */}
      <div className="ut-toolbar">
        <div className="ut-search">
          <input
            type="text"
            className="ut-search__input"
            placeholder="Buscar por nombre, email o móvil…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="Buscar usuarios"
          />
          {appliedFilter && (
            <button
              className="ut-search__clear"
              onClick={handleClear}
              aria-label="Limpiar búsqueda"
            >
              <X size={14} />
            </button>
          )}
          <button
            className="ut-search__btn"
            onClick={handleSearch}
            aria-label="Buscar"
          >
            <Search size={16} />
            Buscar
          </button>
        </div>
        <div className="ut-chips" role="radiogroup" aria-label="Filtro de pago">
          {PAYMENT_FILTERS.map(f => (
            <button
              key={f.value}
              className={`ut-chip${paymentFilter === f.value ? ' ut-chip--active' : ''}`}
              onClick={() => {
                setPaymentFilter(f.value)
                setPage(1)
              }}
              role="radio"
              aria-checked={paymentFilter === f.value}
            >
              <span className="ut-chip__icon">{f.icon}</span>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Results info */}
      {loading && <div className="ut-loading-bar" aria-label="Cargando" />}

      {/* Desktop table */}
      <div className="ut-table-wrap">
        <table className="ut-table" role="table">
          <thead>
            <tr>
              <th scope="col">Usuario</th>
              <th scope="col">Contacto</th>
              <th scope="col">Estado</th>
              <th scope="col">Último pago</th>
              <th scope="col">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr
                key={user._id}
                className={user.hasPaid ? 'ut-row--paid' : 'ut-row--unpaid'}
              >
                <td>
                  <div className="ut-user">
                    <Avatar
                      name={`${user.name} ${user.lastname}`}
                      src={user.avatar}
                      size="sm"
                    />
                    <div className="ut-user__info">
                      <span className="ut-user__name">
                        {user.name} {user.lastname}
                      </span>
                      {user.childs?.length > 0 && (
                        <span className="ut-user__childs">
                          +{user.childs.length} menor
                          {user.childs.length > 1 ? 'es' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td>
                  <div className="ut-contact">
                    <span className="ut-contact__email">{user.email}</span>
                    <span className="ut-contact__phone">{user.phone}</span>
                  </div>
                </td>
                <td>
                  <span
                    className={`ut-badge ${user.hasPaid ? 'ut-badge--success' : 'ut-badge--danger'}`}
                  >
                    {user.hasPaid ? 'Pagado' : 'Pendiente'}
                  </span>
                </td>
                <td>
                  <span className="ut-date">
                    {getLastPaymentDate(user.paymentHistory) || '—'}
                  </span>
                </td>
                <td>
                  <div className="ut-actions">
                    <AlertDialogUpdatePayment
                      user={user}
                      updatePayment={updatePayment}
                    />
                    <AlertDialogResendPassword user={user} />
                    <Link to={`/user/${user._id}`} state={{ user }}>
                      <button
                        className="icon-btn icon-btn--cyan"
                        title="Ver perfil"
                        aria-label={`Ver perfil de ${user.name}`}
                      >
                        <Eye size={16} />
                      </button>
                    </Link>
                    <AlertDialogDeleteUser
                      user={user}
                      getAllUsers={() => setRefreshKey(k => k + 1)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="ut-cards">
        {users.map(user => (
          <div
            key={user._id}
            className={`ut-card ${user.hasPaid ? 'ut-card--paid' : 'ut-card--unpaid'}`}
          >
            <div className="ut-card__top">
              <div className="ut-user">
                <Avatar
                  name={`${user.name} ${user.lastname}`}
                  src={user.avatar}
                  size="sm"
                />
                <div className="ut-user__info">
                  <span className="ut-user__name">
                    {user.name} {user.lastname}
                  </span>
                  <span className="ut-contact__email">{user.email}</span>
                </div>
              </div>
              <span
                className={`ut-badge ${user.hasPaid ? 'ut-badge--success' : 'ut-badge--danger'}`}
              >
                {user.hasPaid ? <CircleCheck size={16} /> : <Clock size={16} />}
              </span>
            </div>
            <div className="ut-card__meta">
              <span>
                <Phone size={14} /> {user.phone}
              </span>
              <span>
                <Calendar size={14} />{' '}
                {getLastPaymentDate(user.paymentHistory) || '—'}
              </span>
            </div>
            {user.childs?.length > 0 && (
              <div className="ut-card__childs">
                {user.childs.map(child => (
                  <div key={child._id} className="ut-card__child">
                    <Avatar
                      name={`${child.name} ${child.lastname}`}
                      src={child.avatar}
                      size="xs"
                    />
                    <span>
                      {child.name} {child.lastname}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="ut-card__actions">
              <AlertDialogUpdatePayment
                user={user}
                updatePayment={updatePayment}
              />
              <AlertDialogResendPassword user={user} />
              <Link to={`/user/${user._id}`} state={{ user }}>
                <button
                  className="icon-btn icon-btn--cyan"
                  title="Ver perfil"
                  aria-label={`Ver perfil de ${user.name}`}
                >
                  <Eye size={16} />
                </button>
              </Link>
              <AlertDialogDeleteUser
                user={user}
                getAllUsers={() => setRefreshKey(k => k + 1)}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Empty state */}
      {!loading && users.length === 0 && (
        <div className="ut-empty">
          <span className="ut-empty__icon">
            <SearchX size={40} />
          </span>
          <p>No se encontraron usuarios</p>
        </div>
      )}

      {/* Pagination */}
      <div className="ut-pagination">
        <div className="ut-pagination__size">
          <label htmlFor="ut-page-size">Mostrar</label>
          <select
            id="ut-page-size"
            className="form-select"
            value={pageSize}
            onChange={e => {
              setPageSize(Number(e.target.value))
              setPage(1)
            }}
          >
            {[25, 50, 100].map(s => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="ut-pagination__nav">
          <button
            className="ut-pagination__btn"
            onClick={() => setPage(1)}
            disabled={page === 1}
            aria-label="Primera página"
          >
            <ChevronsLeft size={16} />
          </button>
          <button
            className="ut-pagination__btn"
            onClick={() => setPage(p => Math.max(p - 1, 1))}
            disabled={page === 1}
            aria-label="Página anterior"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="ut-pagination__info">
            {page} / {totalPages}
          </span>
          <button
            className="ut-pagination__btn"
            onClick={() => setPage(p => (p < totalPages ? p + 1 : p))}
            disabled={page === totalPages}
            aria-label="Página siguiente"
          >
            <ChevronRight size={16} />
          </button>
          <button
            className="ut-pagination__btn"
            onClick={() => setPage(totalPages)}
            disabled={page === totalPages}
            aria-label="Última página"
          >
            <ChevronsRight size={16} />
          </button>
        </div>
      </div>
    </div>
  )
}
